import React from 'react';
import Modal from '../../Components/Modal';
import Form, { Actions, DateTime, TextArea, RequiredValidator, EventTriggers, ItemisationAdjustment, ExternalPayment, Assets, TextField, MoreFields } from '../../Components/Form';
import { useCustomer } from '../../Data/Customers';
import { dateTimeToString } from '../../Services/helpers';
import { eventTriggers } from '../../Data/Misc';
import {useOffline, OfflineQueue} from '../../Components/Offline';
import { Roles, hasRole } from '../../Data/Users';

const MarkAsDone = ({job, onClose, then, actionSource}) => {
    return <Modal title="Mark job as done" isOpen={!!job} setIsOpen={onClose} dismissible>
        {job && <Inner {...{job, then, actionSource}} />}
    </Modal>
}

const Inner = ({job, then, actionSource}) => {
    const {markJobAsDone} = useCustomer(job?.customerId);
    const {isOnline, queueTask, offlineSupported, getQueuedTask} = useOffline();

    const doSubmit = (formData) => {
        const data = {
            addExternalPayment: formData?.externalPayment?.addExternalPayment ?? false,
            paymentMethod: formData?.externalPayment?.paymentMethod ?? null,
            amount: formData?.externalPayment?.amount ?? null,
            actionSource: actionSource ?? 'Individual',
            ...formData,
        };

        if (!isOnline && offlineSupported) {
            return queueTask(
                OfflineQueue.TASK_MARK_JOB_DONE,
                {model: 'Customers/Job', id: job.id, customerId: job.customerId},
                data,
                'Job will be marked as done when you\'re back online'
            );
        }

        return markJobAsDone(job.id, data);
    }

    const queuedTask = getQueuedTask(
        OfflineQueue.TASK_MARK_JOB_DONE,
        {model: 'Customers/Job', id: job.id}
    );
    const data = queuedTask?.data ?? {
        doneOn: dateTimeToString(new Date(), 'T'),
        externalPayment: {
            amount: hasRole(Roles.Worker) ? job?.cost : undefined
        },
        itemisationAdjustment: job.itemisationAdjustment
    }

    return <Form data={data}
            onSubmit={doSubmit}
            afterSubmit={then}
            closeModalOnSubmit
            availableOffline={offlineSupported}
        >
        <DateTime name="doneOn" label="Done at" validators={[RequiredValidator]} defaultNow />
        <TextArea name="notes" label="Notes" />
        <MoreFields title="More options" startExpanded={false}>
            <TextField name="purchaseOrder"
                placeholder={job.purchaseOrder}
                label="Purchase order"
                help="The purchase order reference for this job occurrence only."
            />
            <Assets name="assetIds"
                label="Attachments"
                tags={[{name: 'JobId', value: job.id}]}
                requireFeature="ATTACHMENTS"
            />
            <ExternalPayment name="externalPayment" label="Add external payment" />
            {hasRole(Roles.Worker) && <ItemisationAdjustment originalCost={job.cost} />}

            <EventTriggers triggers={[
                eventTriggers.SEND_NOTIFICATIONS, eventTriggers.INVOICE, eventTriggers.CHARGE, eventTriggers.LOYALTY_CARDS
            ]} />
        </MoreFields>
        <Actions submit={{label: 'Done'}} cancel />
    </Form>
}

export default MarkAsDone;
